import useSWR from 'swr';
import * as companyAPI from '../../api/company/company';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useHosteaToast from '../../ui/feedback/useHosteaToast';
import { setSessionCurrency } from '../../utils/ownerUtils';

// Hook principal pour gérer les entreprises
export const useCompany = () => {
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Récupérer toutes les utilisateurs d'une company
  const fetchCompany = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data: company } = await companyAPI.getMyCompany();
      setCompany(company);
      return company;
    } catch (error) {
      setError(error.message);
      setCompany(null); // Ensure company is always an array
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateCompany = useCallback(async (companyId, companyData) => {
    try {
      setIsSaving(true);

      if (companyData.logoToUpload) {
        try {
          const logoUrl = await companyAPI.uploadCompanyPhoto(companyId, companyData.logoToUpload);
          companyData.logo = logoUrl;
          delete companyData.logoToUpload;
        } catch (uploadError) {
          console.error("Erreur lors de l'upload du nouveau logo:", uploadError);
          console.error("Détails de l'erreur:", uploadError.response?.data);
          throw uploadError;
        }
      }
      const { data: updatedCompany } = await companyAPI.updateCompany(companyId, companyData);

      if (!updatedCompany) throw new Error('Invalid response from server');
      setCompany(updatedCompany);

      return updatedCompany;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    company,
    isLoading,
    isSaving,
    error,
    fetchCompany,
    updateCompany,
  };
};

// Hook pour récupérer une entreprise spécifique
export const useCompanyById = (id) => {
  const { data, error, mutate } = useSWR(
    id ? `/companies/${id}` : null,
    () => companyAPI.getCompanyById(id)
  );

  return {
    company: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour récupérer l'entreprise de l'utilisateur connecté
export const useMyCompany = () => {
  const { data, error, mutate } = useSWR('/companies/my-company', companyAPI.getMyCompany, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 5 * 60 * 1000, // 5 minutes
  });

  // Keep the session currency in sync with the company (idempotent) so every
  // formatAmount() call in the app renders the right symbol ($ for a US company).
  if (data?.data?.currency) setSessionCurrency(data.data.currency);

  return {
    company: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour récupérer l'entreprise d'un utilisateur
export const useCompanyByUserId = (userId) => {
  const { data, error, mutate } = useSWR(
    userId ? `/companies/user/${userId}` : null,
    () => companyAPI.getCompanyByUserId(userId)
  );

  return {
    company: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour les entreprises par type
export const useCompaniesByType = (type) => {
  const { data, error, mutate } = useSWR(
    type ? `/companies/type/${type}` : null,
    () => companyAPI.getCompaniesByType(type)
  );

  return {
    companies: data?.data || [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour la recherche d'entreprises
export const useCompanySearch = (query) => {
  const { data, error, mutate } = useSWR(
    query ? `/companies/search/${query}` : null,
    () => companyAPI.searchCompanies(query)
  );

  return {
    companies: data?.data || [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour le statut Stripe
export const useStripeStatus = (companyId) => {
  const { data, error, mutate } = useSWR(
    companyId ? `/companies/${companyId}/stripe-status` : null,
    () => companyAPI.getStripeStatus(companyId)
  );

  return {
    stripeStatus: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

// Hook pour les opérations de mise à jour avec gestion d'état
export const useCompanyOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useHosteaToast();
  const { t } = useTranslation();

  // ========================================
  // MÉTHODES POUR LES HORAIRES
  // ========================================

  const updateCheckoutTimes = async (companyId, { defaultCheckInTime, defaultCheckOutTime }) => {
    setIsLoading(true);
    try {
      console.log('🔍 [useCompanyOperations] companyId:', companyId);
      console.log('🔍 [useCompanyOperations] companyId type:', typeof companyId);
      console.log('🔍 [useCompanyOperations] updateData:', { defaultCheckInTime, defaultCheckOutTime });

      const response = await companyAPI.updateCompany(companyId, {
        defaultCheckInTime,
        defaultCheckOutTime,
      });

      console.log('✅ [useCompanyOperations] Response:', response);

      toast({
        title: t('hooks.company.checkoutTimesUpdatedTitle', 'Horaires mis à jour'),
        description: t('hooks.company.checkoutTimesUpdatedDesc', 'Les horaires de check-in/check-out ont été mis à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('❌ [useCompanyOperations] Erreur lors de la mise à jour des horaires:', error);
      console.error('❌ [useCompanyOperations] Error response:', error.response);
      console.error('❌ [useCompanyOperations] Error data:', error.response?.data);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.checkoutTimesUpdateError', 'Impossible de mettre à jour les horaires'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const forceUpdateAllUnitsCheckoutTimes = async (companyId, { defaultCheckInTime, defaultCheckOutTime }) => {
    setIsLoading(true);
    try {
      console.log('🔍 [useCompanyOperations] Force update - companyId:', companyId);
      console.log('🔍 [useCompanyOperations] Force update - data:', { defaultCheckInTime, defaultCheckOutTime });

      const response = await companyAPI.forceUpdateAllUnitsCheckoutTimes(companyId, {
        defaultCheckInTime,
        defaultCheckOutTime,
      });

      console.log('✅ [useCompanyOperations] Force update response:', response);

      toast({
        title: t('hooks.company.forceUpdateSuccessTitle', 'Horaires forcés avec succès'),
        description: t('hooks.company.forceUpdateSuccessDesc', 'Horaires mis à jour sur {{count}} unités + company', { count: response.data.unitsUpdated }),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('❌ [useCompanyOperations] Erreur lors de la mise à jour forcée:', error);
      console.error('❌ [useCompanyOperations] Error response:', error.response);
      console.error('❌ [useCompanyOperations] Error data:', error.response?.data);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.forceUpdateError', 'Impossible de forcer la mise à jour des horaires'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getUnitsCount = async (companyId) => {
    try {
      console.log('🔍 [useCompanyOperations] Getting units count for companyId:', companyId);

      const response = await companyAPI.getUnitsCount(companyId);

      console.log('✅ [useCompanyOperations] Units count response:', response);

      return response.data.unitsCount;
    } catch (error) {
      console.error('❌ [useCompanyOperations] Erreur lors du comptage des unités:', error);
      console.error('❌ [useCompanyOperations] Error response:', error.response);
      console.error('❌ [useCompanyOperations] Error data:', error.response?.data);
      throw error;
    }
  };

  // ========================================
  // MÉTHODE GÉNÉRIQUE POUR METTRE À JOUR LA COMPANY
  // ========================================

  const updateCompany = async (companyId, updateData) => {
    setIsLoading(true);
    try {
      console.log('🔍 [useCompanyOperations] Updating company:', companyId);
      console.log('🔍 [useCompanyOperations] Update data:', updateData);

      const response = await companyAPI.updateCompany(companyId, updateData);

      console.log('✅ [useCompanyOperations] Company update response:', response);

      // Toast is handled by the calling component (e.g. CompanySettings.jsx)
      // to avoid duplicate toasts and keep the hook reusable without side effects.
      return response.data;
    } catch (error) {
      console.error('❌ [useCompanyOperations] Erreur lors de la mise à jour de l\'entreprise:', error);
      console.error('❌ [useCompanyOperations] Error response:', error.response);
      console.error('❌ [useCompanyOperations] Error data:', error.response?.data);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // URGENT NOTIFICATIONS (GROUPS)
  // ========================================

  const setUrgentNotificationGroups = async (companyId, groups) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.putUrgentNotificationGroups(companyId, groups);
      toast({
        title: t('hooks.company.groupsUpdatedTitle', 'Groupes mis à jour'),
        description: t('hooks.company.groupsUpdatedDesc', 'Notifications urgentes: groupes remplacés avec succès'),
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error) {
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.groupsUpdateError', 'Impossible de mettre à jour les groupes'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addUrgentNotificationGroup = async (companyId, groupId) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.addUrgentNotificationGroup(companyId, groupId);
      toast({
        title: t('hooks.company.groupAddedTitle', 'Groupe ajouté'),
        description: t('hooks.company.groupAddedDesc', 'Le groupe recevra les notifications urgentes'),
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error) {
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.groupAddError', 'Impossible d\'ajouter le groupe'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const removeUrgentNotificationGroup = async (companyId, groupId) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.removeUrgentNotificationGroup(companyId, groupId);
      toast({
        title: t('hooks.company.groupRemovedTitle', 'Groupe retiré'),
        description: t('hooks.company.groupRemovedDesc', 'Le groupe ne recevra plus les notifications urgentes'),
        status: 'success',
        duration: 3000,
      });
      return response.data;
    } catch (error) {
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.groupRemoveError', 'Impossible de retirer le groupe'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // MÉTHODES POUR LES PRÉFÉRENCES DE CONTACT
  // ========================================

  const updateContactPreferences = async (companyId, contactData) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.updateCompany(companyId, {
        contact: {
          name: contactData.name || '',
          preference: contactData.preference || 'email',
          phone: contactData.phone || '',
          email: contactData.email || '',
        },
      });

      toast({
        title: t('hooks.company.contactPreferencesUpdatedTitle', 'Préférences mises à jour'),
        description: t('hooks.company.contactPreferencesUpdatedDesc', 'Les préférences de contact ont été mises à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.contactPreferencesUpdateError', 'Impossible de mettre à jour les préférences'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // MÉTHODES POUR LES SECTIONS D'INFORMATION
  // ========================================

  const updateInformationSections = async (companyId, sectionsData) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.updateCompany(companyId, {
        'pages.information.defaultTitles': sectionsData,
      });

      toast({
        title: t('hooks.company.sectionsUpdatedTitle', 'Sections mises à jour'),
        description: t('hooks.company.sectionsUpdatedDesc', 'Les sections d\'information ont été mises à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des sections:', error);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.sectionsUpdateError', 'Impossible de mettre à jour les sections'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const addInformationSection = async (companyId, newSection) => {
    try {
      const company = await companyAPI.getCompanyById(companyId);
      const currentSections = company.data?.pages?.information?.defaultTitles || [];
      const updatedSections = [...currentSections, newSection];

      return await updateInformationSections(companyId, updatedSections);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la section:', error);
      throw error;
    }
  };

  const removeInformationSection = async (companyId, sectionIndex) => {
    try {
      const company = await companyAPI.getCompanyById(companyId);
      const currentSections = company.data?.pages?.information?.defaultTitles || [];
      const updatedSections = currentSections.filter((_, index) => index !== sectionIndex);

      return await updateInformationSections(companyId, updatedSections);
    } catch (error) {
      console.error('Erreur lors de la suppression de la section:', error);
      throw error;
    }
  };

  // ========================================
  // MÉTHODES POUR LES INFORMATIONS D'UNITÉ
  // ========================================

  const updateUnitInformation = async (companyId, unitId, unitData) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.updateCompany(companyId, {
        [`units.${unitId}.information`]: unitData,
      });

      toast({
        title: t('hooks.company.unitInfoUpdatedTitle', 'Informations mises à jour'),
        description: t('hooks.company.unitInfoUpdatedDesc', 'Les informations de l\'unité ont été mises à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des informations d\'unité:', error);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.unitInfoUpdateError', 'Impossible de mettre à jour les informations'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUnitAccess = async (companyId, unitId, accessData) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.updateCompany(companyId, {
        [`units.${unitId}.access`]: accessData,
      });

      toast({
        title: t('hooks.company.unitAccessUpdatedTitle', 'Accès mis à jour'),
        description: t('hooks.company.unitAccessUpdatedDesc', 'Les informations d\'accès ont été mises à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'accès:', error);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.unitAccessUpdateError', 'Impossible de mettre à jour l\'accès'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUnitAmenities = async (companyId, unitId, amenitiesData) => {
    setIsLoading(true);
    try {
      const response = await companyAPI.updateCompany(companyId, {
        [`units.${unitId}.amenities`]: amenitiesData,
      });

      toast({
        title: t('hooks.company.amenitiesUpdatedTitle', 'Équipements mis à jour'),
        description: t('hooks.company.amenitiesUpdatedDesc', 'Les équipements ont été mis à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des équipements:', error);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.amenitiesUpdateError', 'Impossible de mettre à jour les équipements'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // MÉTHODES POUR LES NOTIFICATIONS DE PAIEMENT
  // ========================================

  const updatePaymentNotifications = async (companyId, notificationData) => {
    setIsLoading(true);
    try {

      const response = await companyAPI.updateCompany(companyId, {
        paymentNotifications: {
          email: notificationData.email || '',
          enabled: notificationData.enabled !== undefined ? notificationData.enabled : true,
        },
      });

      toast({
        title: t('hooks.company.paymentNotificationsUpdatedTitle', 'Notifications mises à jour'),
        description: t('hooks.company.paymentNotificationsUpdatedDesc', 'Les paramètres de notification de paiement ont été mis à jour avec succès'),
        status: 'success',
        duration: 3000,
      });

      return response.data;
    } catch (error) {
      console.error('❌ [useCompanyOperations] Error data:', error.response?.data);
      toast({
        title: t('common.error', 'Erreur'),
        description: error.response?.data?.message || t('hooks.company.paymentNotificationsUpdateError', 'Impossible de mettre à jour les notifications'),
        status: 'error',
        duration: 3000,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // ========================================
  // MÉTHODES DE VALIDATION
  // ========================================

  const validateTimeFormat = (time) => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const validateContactData = (contactData) => {
    const errors = [];

    if (contactData.preference && !['email', 'whatsapp'].includes(contactData.preference)) {
      errors.push(t('hooks.company.invalidContactPreference', 'Préférence de contact invalide'));
    }

    if (contactData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email)) {
      errors.push(t('hooks.company.invalidEmailFormat', 'Format d\'email invalide'));
    }

    return errors;
  };

  const validatePaymentNotificationEmail = (email) => {
    if (!email) return true; // Email optionnel
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return {
    isLoading,
    updateCompany,
    updateCheckoutTimes,
    forceUpdateAllUnitsCheckoutTimes,
    getUnitsCount,
    updateContactPreferences,
    updateInformationSections,
    addInformationSection,
    removeInformationSection,
    updateUnitInformation,
    updateUnitAccess,
    updateUnitAmenities,
    updatePaymentNotifications,
    setUrgentNotificationGroups,
    addUrgentNotificationGroup,
    removeUrgentNotificationGroup,
    validateTimeFormat,
    validateContactData,
    validatePaymentNotificationEmail,
  };
};

// Fonctions utilitaires pour les opérations CRUD
export const companyUtils = {
  // Créer une entreprise
  createCompany: companyAPI.createCompanyWithValidation,

  // Mettre à jour une entreprise
  updateCompany: companyAPI.updateCompanyWithValidation,

  // Supprimer une entreprise
  deleteCompany: companyAPI.deleteCompanyWithConfirmation,

  // Mettre à jour la configuration Stripe
  updateStripe: companyAPI.updateStripeWithValidation,
}; 